import { useTranslation } from "react-i18next";
import {
  AIAssistantSidebar,
  MobileMenuTrigger,
} from "@/components/ai-assistant/ai-assistant-sidebar";
import {
  AIAssistantContext,
  aiPromptFieldName,
  buildPromptInputSchema,
  type ResponseMessageTypes,
} from "@/components/ai-assistant/context";
import { useFormik } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Chat,
  type ChatHandle,
  type StatusLineHandle,
  type StepEntry,
} from "@/components/ai-assistant/chat";
import {
  companyAiAssistantApi,
  useLazyGetSingleConversationQuery,
  useSpeechToTextMutation,
} from "@/services/(company)/api.ai-assistant";
import { useStreamingChat } from "@/hooks/useStreamingChat";
import { AiChatType } from "@/utils/enums/ai-assistant.enum";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  AI_ASST_MSG_TYPES,
  type IChatAttachment,
  type IChatHistoryItem,
  type ISingleChatBubble,
} from "@/types/(my-space)/ai-assistant.interface";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "usehooks-ts";
import { MessageSquarePlus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  COMPANY_AI_ASSISTANT_SESSION_ID_KEY,
  COMPANY_AI_ASSISTANT_PATHS,
} from "@/routes/company/routes.ai-assistant";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { APIEndpoints } from "@/constants/apiEndpoints";
import { CompanyConversations } from "./_components/conversations";
import { PromptInput } from "@/components/ai-assistant/prompt-input";
import { useMayaAssistantComposer } from "@/components/layout/header/maya-copilot/hooks/use-assistant-composer";
import { FileDropZone } from "@/components/ai-assistant/file-drop-zone";
import {
  detectFencedForm,
  looksLikeFormSpec,
  type MayaFormSpec,
} from "@/components/ai-assistant/maya-form";
import { AdvisorPicker, useAdvisor } from "@/components/ai-assistant/advisor";
import { AssistantHero } from "@/components/ai-assistant/assistant-hero";
import { TempChatToggle } from "@/components/ai-assistant/temp-chat-toggle";
import { CanvasActionButton } from "@/components/ai-assistant/canvas-action-button";
import { advisorStarters } from "@/components/ai-assistant/advisor/company-starters.data";
import { useLocale } from "@/hooks/useLocale";

const VIRTUAL_START = 100_000;

export const CompanyAIAssistant = () => {
  const { t } = useTranslation(["common", "company", "ai-assistant"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("ai-assistant:page_titles.ai_assistant")));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const responseMessageTypeRef = useRef<ResponseMessageTypes>("initial");
  const [isTempChat, setIsTempChat] = useState(false);
  const { sessionId } = useParams<{
    [COMPANY_AI_ASSISTANT_SESSION_ID_KEY]: string;
  }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Below this width the rail is a drawer and the canvas has a real top bar —
  // controls that float over the canvas on desktop belong in that bar instead.
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");

  const forwardToSession = (sid: string) => {
    navigate(COMPANY_AI_ASSISTANT_PATHS.CHAT_SESSION(sid), { replace: true });
  };

  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<ChatHandle>(null);

  // Advisor Mode — front-end only for now. `advisor.slug` is what will ride
  // along as `expertise_slug` on the chat request once the API accepts it; the
  // choice is remembered per conversation, so reopening a chat restores it.
  const { advisor, selectAdvisor } = useAdvisor({
    scope: "company",
    sessionId,
  });
  const [hasChatStarted, setHasChatStarted] = useState(false);
  const {
    sendMessage,
    retryMessage,
    isStreaming: isThinking,
    cancel,
  } = useStreamingChat(APIEndpoints.Company.AIAssistant.SmartChat);
  // Maya's launcher works with this page's composer while it is open (see
  // useMayaAssistantComposer).
  useMayaAssistantComposer(promptInputRef);
  // Set by the mounted PromptInput so the page-level drop zone can funnel files
  // dropped anywhere in the chat area into the composer's upload state.
  const addFilesRef = useRef<(files: FileList | File[]) => void>(() => {});
  // Mirror in a ref so handleRegenerate stays referentially stable (see note in
  // the my-space page) — keeps the ChatRow memo from busting on stream toggles.
  const isThinkingRef = useRef(isThinking);
  isThinkingRef.current = isThinking;
  const statusLineRef = useRef<StatusLineHandle>(null);
  const [stepsMap, setStepsMap] = useState<Record<number, StepEntry[]>>({});
  const currentStepsRef = useRef<StepEntry[]>([]);
  const [history, setHistory] = useState<IChatHistoryItem[]>([]);
  const [activeForm, setActiveForm] = useState<MayaFormSpec | null>(null);
  const messageAttachmentsRef = useRef<Map<number, IChatAttachment[]>>(
    new Map(),
  );

  // SSE `form` field wins, else scan the answer for a fenced ```maya-form block.
  const pickFormSpec = useCallback(
    (answer: string, form: unknown): MayaFormSpec | null =>
      looksLikeFormSpec(form) ? form : detectFencedForm(answer).spec,
    [],
  );
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUAL_START);
  const [getConversation, { isFetching: isFetchingConversation }] =
    useLazyGetSingleConversationQuery();

  const urlToAttachment = (url: string): IChatAttachment => {
    const path = url.split("?")[0];
    const name = decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
    return { name, url };
  };
  const hydrateBubble = (b: ISingleChatBubble): ISingleChatBubble =>
    b.attachmentUrls && b.attachmentUrls.length > 0 && !b.attachments
      ? { ...b, attachments: b.attachmentUrls.map(urlToAttachment) }
      : b;

  const loadMessages = async (
    sid: string,
    pageNum: number,
    isPagination: boolean,
  ) => {
    await getConversation({ sessionId: sid, page: pageNum })
      .unwrap()
      .then((res) => {
        const chronologicalMessages = [...res.data.history]
          .reverse()
          .map((item) => ({
            question: hydrateBubble(item.question),
            answer: hydrateBubble(item.answer),
          }));
        if (chronologicalMessages.length === 0) {
          setHasMore(false);
        } else {
          setHasMore(res.meta.page !== res.meta.lastPage);
          if (isPagination) {
            setHistory((prev) => [...chronologicalMessages, ...prev]);
            setFirstItemIndex(
              (prev) => prev - chronologicalMessages.length * 2,
            );
          } else {
            setHistory(chronologicalMessages);
          }
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error(t("ai-assistant:toasts.failed_to_load_conversation"), {
          id: "ai-error",
        });
      });
  };

  const handleSelectConversation = async (sid: string) => {
    forwardToSession(sid);
    setHasChatStarted(true);
    setActiveForm(null);
    setPage(1);
    setHasMore(true);
    setFirstItemIndex(VIRTUAL_START);
    await loadMessages(sid, 1, false);
  };

  const handleStartReached = () => {
    if (!hasMore || isFetchingConversation || !sessionId) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadMessages(sessionId, nextPage, true);
  };

  const sendPrompt = async (
    promptText: string,
    attachments: IChatAttachment[] = [],
  ) => {
    if (!hasChatStarted) setHasChatStarted(true);
    if (isThinking)
      return toast(t("ai-assistant:toasts.wait_for_ai_to_respond"));
    responseMessageTypeRef.current = "new_response";
    setActiveForm(null);
    currentStepsRef.current = [];
    const optimisticEntry: IChatHistoryItem = {
      question: {
        id: Date.now(),
        content: promptText,
        messageType: AI_ASST_MSG_TYPES.user,
        createdAt: formatDate(new Date(), "hh:mm a"),
        attachments: attachments.length > 0 ? attachments : undefined,
      },
      answer: {
        id: Date.now() + 1,
        content: "...",
        messageType: AI_ASST_MSG_TYPES.assistant,
        createdAt: formatDate(new Date(), "hh:mm a"),
      },
    };
    setHistory((prev) => [...prev, optimisticEntry]);

    const attachmentUrls = attachments.map((a) => a.url);

    await sendMessage(
      {
        message: promptText,
        sessionId: sessionId ?? undefined,
        isTemporary: isTempChat,
        chatType: AiChatType.COMPLEX,
        ...(attachmentUrls.length > 0 ? { attachmentUrls } : {}),
      },
      {
        onMeta: ({ sessionId: sid, lastUserMessageId }) => {
          forwardToSession(sid);
          setHistory((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last)
              next[next.length - 1] = {
                ...last,
                question: { ...last.question, id: lastUserMessageId },
              };
            return next;
          });
        },
        onStatus: (message) => {
          statusLineRef.current?.setStatus(message);
          currentStepsRef.current.push({
            message,
            at: formatDate(new Date(), "hh:mm a"),
          });
        },
        onFinal: ({ answer, form, attachments, attachmentErrors }) => {
          setActiveForm(pickFormSpec(answer, form));
          setHistory((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last)
              next[next.length - 1] = {
                ...last,
                answer: {
                  ...last.answer,
                  content: answer,
                  generatedAttachments: attachments,
                  attachmentErrors,
                },
              };
            return next;
          });
        },
        onMetaFinal: ({ lastAiMessageId }) => {
          setHistory((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last)
              next[next.length - 1] = {
                ...last,
                answer: { ...last.answer, id: lastAiMessageId },
              };
            return next;
          });
          if (currentStepsRef.current.length > 0)
            setStepsMap((prev) => ({
              ...prev,
              [lastAiMessageId]: currentStepsRef.current,
            }));
          if (attachments.length > 0)
            messageAttachmentsRef.current.set(lastAiMessageId, attachments);
        },
        onDone: () => {
          dispatch(
            companyAiAssistantApi.util.invalidateTags([
              "CompanyGetAllConversations",
              "CompanyGetAllQueries",
            ]),
          );
        },
        onError: (err) => {
          toast.error(
            err.message ?? t("ai-assistant:toasts.something_went_wrong"),
          );
          setHistory((prev) => prev.slice(0, -1));
        },
      },
    );
  };

  const handleRegenerate = useCallback(
    (answerId: number) => {
      if (isThinkingRef.current)
        return toast.error(t("ai-assistant:toasts.wait_for_ai_to_respond"));
      responseMessageTypeRef.current = "new_response";
      setActiveForm(null);
      currentStepsRef.current = [];
      setHistory((prev) =>
        prev.map((item) =>
          item.answer.id === answerId
            ? { ...item, answer: { ...item.answer, content: "..." } }
            : item,
        ),
      );
      const retryAttachments =
        messageAttachmentsRef.current.get(answerId) ?? [];
      const retryAttachmentUrls = retryAttachments.map((a) => a.url);
      retryMessage(
        APIEndpoints.Company.AIAssistant.RetryMessage,
        answerId,
        {
          onMeta: () => {},
          onStatus: (message) => {
            statusLineRef.current?.setStatus(message);
            currentStepsRef.current.push({
              message,
              at: formatDate(new Date(), "hh:mm a"),
            });
          },
          onFinal: ({ answer, form, attachments, attachmentErrors }) => {
            setActiveForm(pickFormSpec(answer, form));
            setHistory((prev) =>
              prev.map((item) =>
                item.answer.id === answerId
                  ? {
                      ...item,
                      answer: {
                        ...item.answer,
                        content: answer,
                        generatedAttachments: attachments,
                        attachmentErrors,
                      },
                    }
                  : item,
              ),
            );
          },
          onMetaFinal: ({ lastAiMessageId }) => {
            setHistory((prev) =>
              prev.map((item) =>
                item.answer.id === answerId
                  ? { ...item, answer: { ...item.answer, id: lastAiMessageId } }
                  : item,
              ),
            );
            if (currentStepsRef.current.length > 0)
              setStepsMap((prev) => ({
                ...prev,
                [lastAiMessageId]: currentStepsRef.current,
              }));
            if (retryAttachments.length > 0 && lastAiMessageId !== answerId) {
              messageAttachmentsRef.current.set(
                lastAiMessageId,
                retryAttachments,
              );
              messageAttachmentsRef.current.delete(answerId);
            }
          },
          onDone: () => {
            dispatch(
              companyAiAssistantApi.util.invalidateTags([
                "CompanyGetAllConversations",
                "CompanyGetAllQueries",
              ]),
            );
          },
          onError: (err) => {
            toast.error(
              err.message ?? t("ai-assistant:toasts.something_went_wrong"),
            );
            setHistory((prev) =>
              prev.map((item) =>
                item.answer.id === answerId
                  ? {
                      ...item,
                      answer: {
                        ...item.answer,
                        content: t(
                          "ai-assistant:errors.couldnt_regenerate_response",
                        ),
                      },
                    }
                  : item,
              ),
            );
          },
        },
        retryAttachmentUrls.length > 0 ? retryAttachmentUrls : undefined,
        isTempChat,
        sessionId ?? undefined,
      );
    },
    [retryMessage, pickFormSpec, dispatch, isTempChat, sessionId, t],
  );

  // Stop generation: cancel server-side + abort the local stream, then mark any
  // in-flight optimistic answer (still "...") as stopped.
  const handleStop = useCallback(() => {
    cancel();
    setHistory((prev) =>
      prev.map((item) =>
        item.answer.content === "..."
          ? {
              ...item,
              answer: {
                ...item.answer,
                content: t("ai-assistant:errors.response_stopped"),
              },
            }
          : item,
      ),
    );
  }, [cancel, t]);

  const formik = useFormik({
    initialValues: { [aiPromptFieldName]: "" },
    validationSchema: buildPromptInputSchema(t),
    onSubmit: async ({ prompt }, { resetForm }) => {
      resetForm();
      await sendPrompt(prompt);
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const startNewChat = () => {
    setHasChatStarted(false);
    formik.resetForm();
    setHistory([]);
    setActiveForm(null);
    setFirstItemIndex(VIRTUAL_START);
    navigate(COMPANY_AI_ASSISTANT_PATHS.ROOT);
    dispatch(
      companyAiAssistantApi.util.invalidateTags([
        "CompanyGetAllConversations",
        "CompanyGetAllQueries",
      ]),
    );
  };

  const [highlightMessageId, setHighlightMessageId] = useState<number | null>(
    null,
  );
  const handleSelectQuery = async (sid: string, messageId: number) => {
    forwardToSession(sid);
    setActiveForm(null);
    await getConversation({ sessionId: sid, messageId })
      .unwrap()
      .then((res) => {
        setHistory(res.data.history);
        setFirstItemIndex(VIRTUAL_START);
        setHasChatStarted(true);
        setHighlightMessageId(messageId);
      })
      .catch((e) => {
        console.error(e);
        toast.error(t("ai-assistant:toasts.failed_to_load_conversation"));
      });
  };

  useEffect(() => {
    if (sessionId && history.length === 0 && !isFetchingConversation) {
      setHasChatStarted(true);
      setPage(1);
      setHasMore(true);
      setFirstItemIndex(VIRTUAL_START);
      loadMessages(sessionId, 1, false);
    } else if (!sessionId && hasChatStarted) {
      setHasChatStarted(false);
      setHistory([]);
      setActiveForm(null);
      setFirstItemIndex(VIRTUAL_START);
      setHasMore(true);
      setPage(1);
      formik.resetForm();
    }
  }, [sessionId]);

  const shouldShowGlobalLoader = isFetchingConversation && history.length === 0;

  const startTempChat = () => {
    startNewChat();
    setIsTempChat(true);
  };
  const stopTempChat = () => {
    startNewChat();
    setIsTempChat(false);
  };

  const [speechToText] = useSpeechToTextMutation();
  const transcribe = async (audioBlob: Blob) => {
    const form = new FormData();
    form.append("audio", audioBlob, "recording.webm");
    const res = await speechToText(form).unwrap();
    return res.data?.output ?? "";
  };

  return (
    <AIAssistantContext.Provider
      value={{
        onSelectChat: handleSelectConversation,
        onSelectQuery: handleSelectQuery,
        startNewChat,
        isSidebarOpen,
        setIsSidebarOpen,
        isTempChat,
        startTempChat,
        stopTempChat,
        responseMessageTypeRef,
      }}
    >
      <section className="relative flex size-full bg-white rounded border overflow-hidden">
        <FileDropZone
          className="flex-1 flex flex-col min-w-0"
          onFiles={(files) => addFilesRef.current(files)}
        >
          {/* Sharing lives on the conversation row in the history rail, next to
              its other per-chat actions. On small screens this bar also hosts
              the controls that float over the canvas on desktop. */}
          <MobileMenuTrigger
            title={
              isSmallScreen
                ? t("ai-assistant:page_titles.ai_assistant")
                : undefined
            }
            rightContent={
              isSmallScreen ? (
                <>
                  {hasChatStarted && (
                    <CanvasActionButton
                      icon={MessageSquarePlus}
                      label={t("ai-assistant:chrome.new_chat")}
                      onClick={startNewChat}
                      compact
                    />
                  )}
                  <TempChatToggle compact />
                </>
              ) : undefined
            }
          />
          <div className="relative flex-1 flex flex-col items-center justify-center min-w-0 w-full overflow-hidden">
            <span className="bg-assistant-canvas" />
            {/* Offered on the landing screen only — starting a temporary chat is
                a choice you make before the first message, not mid-conversation.
                The active pill stays put, so the mode is never on silently. */}
            {!isSmallScreen && (!hasChatStarted || isTempChat) && (
              <div className="absolute end-3 top-3 z-20">
                <TempChatToggle />
              </div>
            )}
            <GlobalLoader show={shouldShowGlobalLoader} />
            <AnimatePresence mode="wait">
              {!hasChatStarted ? (
                <motion.div
                  key="greeting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="scrollbar-hidden z-10 mx-auto flex max-h-full w-full min-w-0 max-w-2xl flex-col overflow-y-auto px-3 py-4 text-center sm:px-6 lg:max-w-3xl"
                >
                  <AssistantHero
                    advisor={advisor}
                    onSelectAdvisor={selectAdvisor}
                    onSelectPrompt={(text) => {
                      formik.resetForm();
                      sendPrompt(text);
                    }}
                    starters={advisorStarters(advisor.slug, t)}
                    isTempChat={isTempChat}
                  />
                  <div className="mt-5 sm:mt-7" />
                  <PromptInput
                    isTempChat={isTempChat}
                    formik={formik}
                    isLoading={isThinking}
                    promptInputRef={promptInputRef}
                    layoutId="company-chatbot-prompt-input"
                    transcribe={transcribe}
                    onSend={sendPrompt}
                    onStop={handleStop}
                    exposeAddFiles={(fn) => {
                      addFilesRef.current = fn;
                    }}
                    activeForm={activeForm}
                    onCloseForm={() => setActiveForm(null)}
                    uploadEndpoint={
                      APIEndpoints.Company.AIAssistant.MayaFileUpload
                    }
                    actionSlot={
                      <AdvisorPicker
                        advisor={advisor}
                        onSelect={selectAdvisor}
                        disabled={isThinking}
                      />
                    }
                    placeholder={t(advisor.placeholder)}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="h-full w-full min-w-0 z-10"
                >
                  <Chat
                    ref={chatRef}
                    history={history}
                    hasMore={hasMore}
                    firstItemIndex={firstItemIndex}
                    setHistory={setHistory}
                    sessionId={sessionId ?? null}
                    onStartReached={handleStartReached}
                    highlightMessageId={highlightMessageId}
                    setHighlightMessageId={setHighlightMessageId}
                    isFetchingNextPage={isFetchingConversation}
                    statusLineRef={statusLineRef}
                    onRegenerate={handleRegenerate}
                    stepsMap={stepsMap}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* `relative z-20`: `.surface-frosted` sets `backdrop-filter`, which
              makes this bar its own stacking context. The Maya form card inside
              it is absolutely positioned above the composer, so its own `z-30`
              could no longer compete with the `z-10` message list — the list
              painted over the card and swallowed every click on it. Lifting the
              whole bar fixes it. */}
          {hasChatStarted && (
            <div className="surface-frosted relative z-20 min-h-16 w-full border-t p-2">
              <PromptInput
                isTempChat={isTempChat}
                formik={formik}
                isLoading={isThinking}
                promptInputRef={promptInputRef}
                layoutId="company-chatbot-prompt-input"
                transcribe={transcribe}
                onSend={sendPrompt}
                onStop={handleStop}
                exposeAddFiles={(fn) => {
                  addFilesRef.current = fn;
                }}
                activeForm={activeForm}
                onCloseForm={() => setActiveForm(null)}
                uploadEndpoint={APIEndpoints.Company.AIAssistant.MayaFileUpload}
                actionSlot={
                  <AdvisorPicker
                    advisor={advisor}
                    onSelect={selectAdvisor}
                    disabled={isThinking}
                  />
                }
                placeholder={t(advisor.placeholder)}
              />
            </div>
          )}
        </FileDropZone>
        <AIAssistantSidebar
          startNewChat={startNewChat}
          conversationsSlot={<CompanyConversations />}
          showTempChat={false}
        />
      </section>
    </AIAssistantContext.Provider>
  );
};

const GlobalLoader = ({ show }: { show: boolean }) =>
  !show ? null : <Spinner wrapperClassName="absolute inset-0 z-10" />;
